import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import { PublicShell } from "./PublicHome";
import {
  Menu, Bell, LogIn, ClipboardEdit, PenLine,
  Gift, Store, FileText, MapPin, Phone, Clock, X as XIcon,
  ArrowRight, ChevronLeft, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightweight, dependency-free carousel — reference layout is dot-nav, auto-cycle 5s
function HeroCarousel({ slides }) {
  const [idx, setIdx] = useState(0);
  const n = slides.length;
  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n]);
  if (n === 0) return null;
  const s = slides[idx];
  const go = (d) => setIdx((i) => (i + d + n) % n);
  return (
    <div className="relative rounded-[28px] overflow-hidden bg-white shadow-[0_10px_40px_-15px_rgba(0,0,0,0.15)] ring-1 ring-black/5" data-testid="landing-hero-carousel">
      <div className="aspect-[4/5] sm:aspect-[16/10] relative">
        {s.image ? (
          <img
            src={s.image}
            alt={s.title || "Promo"}
            className="w-full h-full object-cover"
            data-testid={`landing-hero-slide-${idx}`}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-red-50 via-white to-red-100 flex flex-col items-start justify-center p-8">
            <div className="text-[11px] font-bold tracking-[0.2em] text-red-600 uppercase mb-2">
              {s.eyebrow || "Raccolta Punti"}
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold leading-tight text-zinc-900 max-w-[70%]">
              {s.title || "Se non partecipi… non sai che ti perdi!"}
            </h2>
            {s.subtitle && (
              <p className="text-sm text-zinc-600 mt-3 max-w-[70%]">{s.subtitle}</p>
            )}
          </div>
        )}
        {n > 1 && (
          <>
            <button
              aria-label="Precedente"
              onClick={() => go(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-zinc-700"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              aria-label="Successivo"
              onClick={() => go(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-zinc-700"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
      {n > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2" data-testid="landing-hero-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`landing-hero-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-zinc-800" : "w-1.5 bg-zinc-400/50"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionTile({ to, href, onClick, icon: Icon, label, testid }) {
  const content = (
    <div className="w-full h-24 sm:h-28 rounded-2xl bg-[#1E40AF] hover:bg-[#1E3A8A] active:scale-[0.98] transition-all shadow-[0_6px_18px_-6px_rgba(30,64,175,0.55)] flex items-center justify-center">
      <Icon className="h-8 w-8 text-white" strokeWidth={2.2} />
    </div>
  );
  const wrapper = "flex flex-col items-center gap-2 focus:outline-none";
  if (to) {
    return (
      <Link to={to} className={wrapper} data-testid={testid}>
        {content}
        <span className="text-[15px] font-semibold text-zinc-800">{label}</span>
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={wrapper} data-testid={testid}>
        {content}
        <span className="text-[15px] font-semibold text-zinc-800">{label}</span>
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={wrapper} data-testid={testid}>
      {content}
      <span className="text-[15px] font-semibold text-zinc-800">{label}</span>
    </button>
  );
}

function SideMenu({ open, onClose }) {
  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="landing-menu-overlay"
      />
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-white z-50 shadow-2xl transform transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="landing-menu"
      >
        <div className="p-4 flex items-center justify-between border-b border-zinc-100">
          <span className="font-heading font-bold text-lg text-zinc-900">Menu</span>
          <button onClick={onClose} aria-label="Chiudi menu" className="p-2 rounded-md hover:bg-zinc-100">
            <XIcon className="h-5 w-5 text-zinc-700" />
          </button>
        </div>
        <nav className="p-2 space-y-1 text-sm">
          {[
            { to: "/login", label: "Accedi alla tua card", icon: LogIn },
            { to: "/registrati", label: "Registra la card", icon: ClipboardEdit },
            { to: "/richiedi-card", label: "Richiedi una card", icon: PenLine },
            { to: "/home/sedi", label: "Punti vendita", icon: Store },
          ].map((it) => (
            <Link
              key={it.to}
              to={it.to}
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-700 hover:bg-red-50 hover:text-red-700"
              data-testid={`landing-menu-link-${it.label.split(" ")[0].toLowerCase()}`}
            >
              <it.icon className="h-4 w-4" />
              <span>{it.label}</span>
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
}

export default function PublicLanding() {
  const nav = useNavigate();
  const { settings } = useAppSettings();
  const logo = resolveImageUrl(settings.logo_url);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [slides, setSlides] = useState([]);

  // Public promo slides: prefer active-campaign images / defaults
  useEffect(() => {
    axios.get(`${API}/public/promos`)
      .then(({ data }) => setSlides((data || []).filter(Boolean)))
      .catch(() => setSlides([]));
  }, []);

  const heroSlides = useMemo(() => {
    if (slides.length > 0) return slides;
    // Graceful fallback so the UI never looks empty
    return [
      {
        eyebrow: "Raccolta Punti",
        title: "Se non partecipi… non sai che ti perdi!",
        subtitle: `Attiva la tua ${settings.app_name || "card"} e inizia a guadagnare punti su ogni acquisto.`,
      },
      {
        eyebrow: "Nuovi premi",
        title: "Riscatta gadget e sconti esclusivi",
        subtitle: "Scopri il catalogo premi aggiornato ogni mese.",
      },
      {
        eyebrow: "Solo per te",
        title: "Coupon riservati ai soci",
        subtitle: "Offerte lampo e sconti dedicati direttamente nella tua card.",
      },
    ];
  }, [slides, settings.app_name]);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="landing-page">
      <Toaster position="top-center" richColors closeButton />
      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-zinc-100">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="Apri menu"
            data-testid="landing-menu-button"
            className="h-11 w-11 rounded-2xl bg-white shadow-sm ring-1 ring-zinc-100 flex items-center justify-center text-red-600 hover:bg-red-50"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2" data-testid="landing-logo">
            {logo ? (
              <img src={logo} alt={settings.app_name} className="h-11 object-contain max-w-[180px]" />
            ) : (
              <span className="font-heading font-extrabold text-xl text-red-600">
                {settings.app_name || "Loyalty Club"}
              </span>
            )}
          </div>
          <button
            onClick={() => setNotifOpen(true)}
            aria-label="Notifiche"
            data-testid="landing-notif-button"
            className="h-11 w-11 rounded-2xl bg-white shadow-sm ring-1 ring-zinc-100 flex items-center justify-center text-red-600 hover:bg-red-50 relative"
          >
            <Bell className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 pb-16 pt-4 space-y-8">
        {/* Hero */}
        <HeroCarousel slides={heroSlides} />

        {/* Primary actions */}
        <section className="grid grid-cols-3 gap-4" data-testid="landing-primary-actions">
          <ActionTile to="/login" icon={LogIn} label="Accedi" testid="landing-btn-accedi" />
          <ActionTile to="/registrati" icon={ClipboardEdit} label="Registra Card" testid="landing-btn-registra" />
          <ActionTile to="/richiedi-card" icon={PenLine} label="Richiedi Card" testid="landing-btn-richiedi" />
        </section>

        <div className="border-t border-zinc-200" />

        {/* Secondary actions */}
        <section className="grid grid-cols-3 gap-4" data-testid="landing-secondary-actions">
          <ActionTile
            onClick={() => { const el = document.getElementById("promo-section"); el?.scrollIntoView({ behavior: "smooth" }); }}
            icon={Gift}
            label="Raccolta Punti"
            testid="landing-btn-raccolta"
          />
          <ActionTile to="/home/sedi" icon={Store} label="Punti vendita" testid="landing-btn-sedi" />
          <ActionTile to="/cliente/accedi" icon={FileText} label="Volantino" testid="landing-btn-volantino" />
        </section>

        {/* Info / promo details anchor */}
        <section id="promo-section" className="pt-4 space-y-3">
          <h3 className="font-heading font-extrabold text-xl">Come funziona</h3>
          <ol className="space-y-2 text-sm text-zinc-700">
            {[
              "Attiva la tua card in negozio o richiedila online.",
              "Ogni euro speso vale 1 punto (con moltiplicatori nelle campagne attive).",
              "Riscatta premi e sconti esclusivi direttamente dal tuo portale.",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 h-6 w-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ol>
          <button
            onClick={() => nav("/registrati")}
            data-testid="landing-cta-register"
            className="mt-2 inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold text-sm"
          >
            Inizia ora <ArrowRight className="h-4 w-4" />
          </button>
        </section>
      </main>

      {/* Bottom notifications drawer (light) */}
      {notifOpen && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setNotifOpen(false)}>
          <div
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
            data-testid="landing-notif-panel"
          >
            <div className="flex items-center justify-between">
              <div className="font-heading font-bold text-lg">Notifiche</div>
              <button onClick={() => setNotifOpen(false)} className="p-1.5 hover:bg-zinc-100 rounded-md">
                <XIcon className="h-5 w-5 text-zinc-600" />
              </button>
            </div>
            <p className="text-sm text-zinc-500">
              Accedi alla tua card per ricevere notifiche personalizzate su punti, premi e offerte.
            </p>
            <Link to="/login" className="block w-full text-center px-4 py-3 rounded-xl bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-semibold">
              Accedi ora
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Punti vendita (public branch list) ---- */
export function PublicBranches() {
  const { settings } = useAppSettings();
  const logo = resolveImageUrl(settings.logo_url);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/public/branches`)
      .then(({ data }) => setBranches(data || []))
      .catch(() => setBranches([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <PublicShell title="Punti vendita" back="/home" testid="public-branches">
      <div className="space-y-3" data-testid="branches-list">
        {loading && <div className="text-sm text-zinc-500 text-center py-10">Caricamento…</div>}
        {!loading && branches.length === 0 && (
          <div className="text-sm text-zinc-500 text-center py-10">Nessuna sede pubblicata.</div>
        )}
        {branches.map((b) => (
          <article key={b.id} data-testid={`branch-${b.id}`} className="rounded-2xl border border-zinc-200 p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-zinc-900">{b.name}</h3>
                {b.address && <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {b.address}</div>}
              </div>
              {b.logo_url && <img src={resolveImageUrl(b.logo_url)} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-10 w-10 object-contain rounded-lg" />}
            </div>
            {(b.phone || b.hours) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-600">
                {b.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {b.phone}</span>}
                {b.hours && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {b.hours}</span>}
              </div>
            )}
            {b.address && (
              <a
                href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(b.address)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--client-brand, #1E40AF)" }}
                data-testid={`branch-map-${b.id}`}
              >
                Apri in mappa <ArrowRight className="h-3 w-3" />
              </a>
            )}
          </article>
        ))}
      </div>
    </PublicShell>
  );
}

/* ---- Volantino (public flyers view with inline PDF preview) ---- */
export function PublicFlyer() {
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfViewer, setPdfViewer] = useState(null); // { title, url }
  // Optional per-branch filter driven by URL/localStorage
  const params = new URLSearchParams(window.location.search);
  const branchQ = params.get("branch") || (typeof localStorage !== "undefined" ? localStorage.getItem("vivi:branch_id") : null);
  useEffect(() => {
    const q = branchQ ? `?branch_id=${encodeURIComponent(branchQ)}` : "";
    axios.get(`${API}/public/flyers${q}`)
      .then(({ data }) => setFlyers(data || []))
      .catch(() => setFlyers([]))
      .finally(() => setLoading(false));
  }, [branchQ]);
  const abs = (u) => {
    if (!u) return null;
    if (/^https?:\/\//.test(u)) return u;
    return `${process.env.REACT_APP_BACKEND_URL}${u.startsWith("/") ? u : `/${u}`}`;
  };
  return (
    <PublicShell title="Volantini" back="/home" testid="public-volantini">
      <div className="space-y-4" data-testid="flyer-list">
        {loading && <div className="text-sm text-zinc-500 text-center py-10">Caricamento…</div>}
        {!loading && flyers.length === 0 && (
          <div className="text-sm text-zinc-500 text-center py-10">Nessuna promo attiva al momento.</div>
        )}
        {flyers.map((n) => (
          <article key={n.id} data-testid={`flyer-item-${n.id}`} className="rounded-2xl overflow-hidden border border-zinc-200 bg-white shadow-sm">
            {n.cover_url && (
              <img src={resolveImageUrl(n.cover_url)} alt={n.title} onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full aspect-video object-cover" />
            )}
            <div className="p-4 space-y-2">
              <h3 className="font-heading font-bold text-zinc-900">{n.title}</h3>
              {n.description && <p className="text-sm text-zinc-600 whitespace-pre-line">{n.description}</p>}
              {(n.valid_from || n.valid_until) && (
                <div className="text-[11px] text-zinc-500">
                  Valido {n.valid_from ? `dal ${n.valid_from.slice(0,10)}` : ""} {n.valid_until ? `al ${n.valid_until.slice(0,10)}` : ""}
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                {n.pdf_url && (
                  <>
                    <button
                      onClick={() => setPdfViewer({ title: n.title, url: abs(n.pdf_url) })}
                      data-testid={`flyer-preview-${n.id}`}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-white text-sm font-semibold hover:opacity-90" style={{ background: "var(--client-brand, #1E40AF)" }}
                    >
                      <FileText className="h-4 w-4" /> Anteprima PDF
                    </button>
                    <a
                      href={abs(n.pdf_url)}
                      download
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`flyer-download-${n.id}`}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border bg-white text-sm font-semibold hover:bg-zinc-50" style={{ borderColor: "var(--client-brand, #1E40AF)", color: "var(--client-brand, #1E40AF)" }}
                    >
                      Scarica
                    </a>
                  </>
                )}
                {n.external_link && (
                  <a href={n.external_link} target="_blank" rel="noopener noreferrer"
                     data-testid={`flyer-ext-${n.id}`}
                     className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border bg-white text-sm font-semibold hover:bg-zinc-50" style={{ borderColor: "var(--client-brand, #1E40AF)", color: "var(--client-brand, #1E40AF)" }}>
                    Scopri di più <ArrowRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Full-screen PDF preview modal (uses browser's native PDF viewer via <iframe>) */}
      {pdfViewer && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="pdf-preview-modal">
          <div className="flex items-center justify-between p-3 bg-black text-white">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate text-sm font-medium">{pdfViewer.title}</span>
            </div>
            <div className="flex items-center gap-2">
              <a href={pdfViewer.url} download target="_blank" rel="noopener noreferrer"
                 className="text-xs px-3 py-1.5 rounded-lg border border-white/30 hover:bg-white/10"
                 data-testid="pdf-preview-download">Scarica</a>
              <button
                onClick={() => setPdfViewer(null)}
                data-testid="pdf-preview-close"
                className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                aria-label="Chiudi"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 bg-zinc-100">
            {/* Native browser PDF viewer — works on Chrome/Firefox/Safari desktop and Android.
                On iOS Safari, an <iframe> of a PDF doesn't always render; we fall back to
                <object> + a download link inside its slot. */}
            <object
              data={`${pdfViewer.url}#toolbar=0&navpanes=0`}
              type="application/pdf"
              className="w-full h-full"
              data-testid="pdf-preview-object"
            >
              <div className="p-6 text-center text-sm text-zinc-700">
                Il tuo dispositivo non può mostrare il PDF in anteprima.
                <a href={pdfViewer.url} download className="ml-2 underline" style={{ color: "var(--client-brand, #1E40AF)" }}>Scarica il volantino</a>
              </div>
            </object>
          </div>
        </div>
      )}
    </PublicShell>
  );
}
